import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import './TodayList.css';
import useAuth from "../../Auth/useAuth";
import { useNavigate } from 'react-router-dom';
import MapComponent from "../MapView/MapComponent";
import "../MapView/MapComponent.css";
import config from '../../config';
import FreightMapComponent from '../FreightMapComponent/FreightMapComponent';
import AlertModal from '../AlertModal/AlertModal';

axios.defaults.baseURL = config.BASE_URL;

const TodayList = () => {
  const { userProfile, setTripIDState, moduleChange, setModuleChange, activeModule, setActiveModule } = useAuth();
  const Navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [progress, setProgress] = useState({});
  const [disabled, setIsDisabled] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [backupSortedData, setBackupSortedData] = useState([]);
  const [tripDuration, setTripDuration] = useState({});
  const [showMapModal, setShowMapModal] = useState(false);
  const [mapData, setMapData] = useState({
    from: '',
    to: '',
    tripId: '',
    DriverName: '',
    username: '',
    profile: '',
  });
  const [showFreightMap, setShowFreightMap] = useState(false);

  // Add driver list state
  const [driverList, setDriverList] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [activeDriverRowId, setActiveDriverRowId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Create ref for driver dropdown
  const driverDropdownRef = useRef(null);
  const driverInputRefs = useRef({});

  // Modal handler functions
  const showModal = (title, message, type = 'info') => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Fetch driver list function
  const fetchDriverList = async () => {
    try {
      const response = await axios.get("/drivers-list", {
        params: { status: "Active" },
        withCredentials: true,
      });
      console.log("Driver List:", response.data);
      setDriverList(response.data.resultDrivers || []);
    } catch (error) {
      console.error("Error fetching driver list:", error);
      showModal('Error', 'Failed to load driver list', 'error');
    }
  };

  // Calculate dropdown position
  const calculateDropdownPosition = (inputElement) => {
    if (!inputElement) return { top: 0, left: 0 };
    
    const rect = inputElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    return {
      top: rect.bottom + scrollTop + 4,
      left: rect.left + scrollLeft,
      width: rect.width
    };
  };

  // Filter drivers based on search input
  const filterDrivers = (searchTerm, rowId) => {
    if (!searchTerm || driverList.length === 0) {
      setFilteredDrivers([]);
      return;
    }

    const filtered = driverList.filter((driver) =>
      driver.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (driver.fullName && driver.fullName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredDrivers(filtered);
    setActiveDriverRowId(rowId);
  };

  // Handle driver selection
  const handleDriverSelect = (driver, rowId) => {
    const newData = tableData.map(item =>
      item._id === rowId
        ? { ...item, DriverName: driver.username }
        : item
    );
    setTableData(newData);
    setShowDriverDropdown(false);
    setActiveDriverRowId(null);
  };

  // Handle driver input click
  const handleDriverInputClick = (rowId) => {
    const inputElement = driverInputRefs.current[rowId];
    if (inputElement) {
      const position = calculateDropdownPosition(inputElement);
      setDropdownPosition(position);
    }
    
    const row = tableData.find(item => item._id === rowId);
    if (row) {
      filterDrivers(row.DriverName, rowId);
      setShowDriverDropdown(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close driver dropdown if click is outside
      if (showDriverDropdown && 
          driverDropdownRef.current && 
          !driverDropdownRef.current.contains(event.target) &&
          !event.target.closest('.driver-input')) {
        setShowDriverDropdown(false);
        setActiveDriverRowId(null);
      }

      // Existing click outside logic for edit mode
      if (!event.target.closest('.d3')) {
        if (editMode) {
          setTableData([...backupSortedData]);
        }
        setEditingRow(null);
        setIsDisabled(false);
        setSelectedFiles({});
        setEditMode(false);
        setProgress(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editMode, backupSortedData, showDriverDropdown]);

  // Update dropdown position on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (showDriverDropdown && activeDriverRowId) {
        const inputElement = driverInputRefs.current[activeDriverRowId];
        if (inputElement) {
          const position = calculateDropdownPosition(inputElement);
          setDropdownPosition(position);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showDriverDropdown, activeDriverRowId]);

  // Initialize
  useEffect(() => {
    sessionStorage.setItem('actMod', 'today-list');
    setActiveModule(sessionStorage.getItem('actMod'));
    fetchDriverList(); // Fetch drivers on component mount
  }, []);

  // Existing functions remain the same...
  const handleDownload = async (id, field, originalname) => {
    try {
      const response = await axios.get(`/api/download/${id}/${field}`,
        {
          withCredentials: true,
          responseType: 'blob'
        });

      const blob = new Blob([response.data], {
        type: response.headers['content-type']
      });

      saveAs(blob, originalname || 'download');

    } catch (error) {
      console.error('Download error:', error);
      showModal('Download Error', 'Error downloading file. Please try again.', 'error');
    }
  };

  const handleEdit = (rowId) => {
    setBackupSortedData([...tableData]);
    setEditingRow(rowId);
    setIsDisabled(false);
    setSelectedFiles({});
    setEditMode(true);
  };

  const handleFileSelect = async (e, rowId, fieldName) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    if ((file.size / (1024 * 1024)) > 1) {
      e.target.value = '';
      showModal('File Size Error', `File size is ${(file.size / (1024 * 1024)).toFixed(2)} mb. Please upload file upto 1 mb`, 'error');
      return;
    }

    setSelectedFiles({
      ...selectedFiles,
      [`${rowId}-${fieldName}`]: file
    });
  };

  const handleSave = async (row) => {
    if (!row.TransactionStatus) {
      showModal('Validation Error', 'Please select a valid transaction status', 'warning');
      return;
    }

    setIsDisabled(true);
    setProgress({});

    const formData = new FormData();
    formData.append('id', row._id);
    formData.append('transactionStatus', row.TransactionStatus);
    formData.append('weight', row.Weight);
    formData.append('actualWeight', row.ActualWeight);
    formData.append('short', row.Short);
    formData.append('driverName', row.DriverName); // Add driver name to form data

    const fieldNames = {};
    Object.keys(selectedFiles).forEach(key => {
      if (key.startsWith(row._id)) {
        const fieldName = key.split('-')[1];
        formData.append(fieldName, selectedFiles[key]);
        fieldNames[fieldName] = 0;
      }
    });

    try {
      const response = await axios.post(`/api/reports/${row._id}`, formData, {
        withCredentials: true,
        onUploadProgress: (ProgressEvent) => {
          const percentCompleted = Math.round((ProgressEvent.loaded * 100) / ProgressEvent.total);

          setProgress((prevProgress) => ({
            ...prevProgress,
            ...Object.keys(fieldNames).reduce((acc, name) => {
              acc[name] = percentCompleted;
              return acc;
            }, {}),
          }))
        }
      });

      // Refresh data
      const updatedResponse = await axios.get('/api/reports', {
        params: todayFilters,
        withCredentials: true,
      });
      setProgress({});
      setIsDisabled(false);

      setTableData(updatedResponse.data.tableData);
      setBackupSortedData(updatedResponse.data.tableData);
      setEditingRow(null);
      setSelectedFiles({});
      setShowDriverDropdown(false);
      setActiveDriverRowId(null);

      if (response.data && response.data.message) {
        console.log('response:', response.data.message);
        setTimeout(() => {
          showModal('Success', response.data.message, 'success');
        }, 500);
      }
    } catch (error) {
      setProgress({});
      setIsDisabled(false);
      if (error.response.data.message) {
        alert(error.response.data.message);
      }
      else {
        showModal('Update Error', 'Error updating the record. Please try again.', 'error');
      }
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const todayEnd = new Date(today);
  todayEnd.setHours(23, 59, 59, 999);

  function formatToYYYYMMDD(date) {
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'Asia/Kolkata'
    }).split('/').reverse().join('-');
  }

  const todayFilters = {
    startDate: formatToYYYYMMDD(today),
    endDate: formatToYYYYMMDD(todayEnd),
    vendor: '',
    truckNumber: ''
  };

  useEffect(() => {
    const todayList = async () => {
      try {
        const response = await axios.get('/api/reports', {
          params: todayFilters,
          withCredentials: true
        });
      console.log('today table data :', response.data.tableData);

       setTableData(response.data.tableData);
      } catch (error) {
        console.error(`Error fetching Today's list:`, error);
        let errorMessage = `Error fetching Today's list`;
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        console.error(errorMessage);
      }
    }
    todayList();
  }, []);

  const formatTime = (time) => {
    const [timePart, ampm] = time.split(' ');
    const [hour, minutes] = timePart.split(':');
    return `${hour}:${minutes} ${ampm}`;
  };

  const downloadExcel = () => {
    const ExcelData = tableData.map(row => {
      const { DieselSlipImage, LoadingAdvice, InvoiceCompany, WeightmentSlip, _id, TripID, Username, Profile, TruckNumber, DONumber, createdAt, updatedAt, __v, DriverName, Vendor, DestinationFrom, DestinationTo, TruckType, TransactionStatus, Weight, ActualWeight, DifferenceInWeight, Freight, DieselAmount, DieselSlipNumber, TDS_Rate, Short, Advance, Toll, Adblue, Greasing, BillId, localDate, localTime } = row;

      return {
        "Trip ID": TripID,
        "Username": Username,
        "Profile": Profile,
        "Truck Number": TruckNumber,
        "DO Number": DONumber,
        "Date": localDate,
        "Time": formatTime(localTime),
        "Driver Name": DriverName,
        "Vendor": Vendor,
        "Destination From": DestinationFrom,
        "Destination To": DestinationTo,
        "Truck Type": TruckType,
        "Transaction Status": TransactionStatus,
        "Weight": Weight,
        "Actual Weight": ActualWeight,
        "Difference In Weight": DifferenceInWeight,
        ...((userProfile !== 'loadingManager' && userProfile !== 'unloadingManager') && { "Freight": Freight }),   // Conditionally include Freight
        "Diesel Amount": DieselAmount,
        "Diesel Slip Number": DieselSlipNumber,
        "TDS Amount": (Freight * (TDS_Rate / 100)).toFixed(2),
        "Short": Short,
        "Advance": Advance,
        "Toll": Toll,
        "Adblue": Adblue,
        "Greasing": Greasing,
        "Bill ID": BillId
      }
    });

    const worksheet = XLSX.utils.json_to_sheet(ExcelData);
    // Set column widths
    worksheet['!cols'] = [
      { wch: 10 },  // Trip ID
      { wch: 15 },  // Username
      { wch: 20 },  // Profile
      { wch: 12 },  // Truck Number
      { wch: 12 },  // DO Number
      { wch: 12 },  // Date
      { wch: 10 },  // Time
      { wch: 15 },  // Driver Name
      { wch: 15 },  // Vendor
      { wch: 18 },  // Destination From
      { wch: 18 },  // Destination To
      { wch: 15 },  // Truck Type
      { wch: 20 },  // Transaction Status
      { wch: 10 },  // Weight
      { wch: 15 },  // Actual Weight
      { wch: 15 },  // Difference In Weight
      (userProfile !== 'loadingManager' && userProfile !== 'unloadingManager') ? { wch: 15 } : null,   // Freight
      { wch: 15 },  // Diesel Amount
      { wch: 15 },  // Diesel Slip Number
      { wch: 10 },  // TDS Rate
      { wch: 10 },  // Short
      { wch: 10 },  // Advance
      { wch: 10 },  // Toll
      { wch: 10 },  // Adblue
      { wch: 10 },  // Greasing
      { wch: 10 }   // Bill ID
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reports');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array', cellStyles: true });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, 'TodayList.xlsx');
  };

  const groupDataByDate = (data) => {
    const grouped = {};
    data.forEach(item => {
      const date = item.localDate;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(item);
    });
    return grouped;
  };

    function formatToReadableDate(dateStr) {
    const [dd, mm, yyyy] = dateStr.split('/');

    const day = parseInt(dd, 10);
    const monthIndex = parseInt(mm, 10) - 1; // JS months are 0-based
    const year = parseInt(yyyy, 10);

    const date = new Date(year, monthIndex, day);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    // Add ordinal suffix
    function getDayWithSuffix(day) {
      if (day > 3 && day < 21) return `${day}th`;
      switch (day % 10) {
        case 1: return `${day}st`;
        case 2: return `${day}nd`;
        case 3: return `${day}rd`;
        default: return `${day}th`;
      }
    }

    return `${getDayWithSuffix(day)} ${monthNames[monthIndex]}, ${year}`;
  }
  const handleOpenFreightMap = () => {
    setShowFreightMap(true);
  };

  return (
    <div className="todayList-container">
      <AlertModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
      
      {tableData.length === 0 && (
        <div className="no-data-message">
          <p>No records available</p>
        </div>
      )}

      {tableData.length > 0 && (
        <div className="table-box">
          {Object.entries(groupDataByDate(tableData)).map(([date, rows]) => (
            <div key={date} className={`table-responsive ${rows.some(row => editingRow === row._id) ? 'd3' : ''}`}>
              <table>
                <thead id='sticky-header'>
                  <tr>
                    <th>Trip ID</th>
                    <th>Locate</th>
                    <th>Username</th>
                    <th>Profile</th>
                    <th>Truck Number</th>
                    <th>DO Number</th>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Driver Name</th>
                    <th>Vendor</th>
                    <th>Destination From</th>
                    <th>Destination To</th>
                    <th>Truck Type</th>
                    <th>Transaction Status</th>
                    <th>Weight</th>
                    <th>Actual Weight</th>
                    <th>Difference In Weight</th>
                    {(userProfile !== 'loadingManager' && userProfile !== 'unloadingManager') && <th>Freight</th>}
                    <th>Diesel Amount</th>
                    <th>Diesel Slip Number</th>
                    <th>TDS Amount</th>
                    <th>Short</th>
                    <th>Advance</th>
                    <th>Toll</th>
                    <th>Adblue</th>
                    <th>Greasing</th>
                    <th>Billing Id</th>
                    <th className='uploadTD'>Diesel Slip</th>
                    <th className='uploadTD'>Loading Advice</th>
                    <th className='uploadTD'>Invoice Company</th>
                    <th className='uploadTD'>Weightment Slip</th>
                    {userProfile !== 'loadingManager' && <th>Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr key={row._id}>
                      <td
                        onClick={userProfile !== 'loadingManager' ? () => {
                          setModuleChange('/home');
                          setTripIDState(row);
                          Navigate(`/home/${row.TripID}`, { state: row });
                        } : undefined}
                        className={userProfile !== 'loadingManager' ? 'trip-id' : ''}
                      >
                        {row.TripID}
                      </td>
                      <td
                        className='locate-icon'
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          setMapData({
                            from: row.DestinationFrom,
                            to: row.DestinationTo,
                            tripId: row.TripID,
                            DriverName: row.DriverName,
                            username: row.Username,
                            profile: row.Profile,
                          });
                          setShowMapModal(true);
                        }}
                      >
                        🧭
                      </td>
                      <td>{row.Username}</td>
                      <td>{row.Profile}</td>
                      <td>{row.TruckNumber}</td>
                      <td>{row.DONumber}</td>
                      <td>{row.localDate}</td>
                      <td>{row.localTime}</td>
                      <td>
                        {editingRow === row._id ? (
                          <div style={{ position: 'relative' }}>
                            <input
                              ref={el => driverInputRefs.current[row._id] = el}
                              className='editInput driver-input'
                              type="text"
                              value={row.DriverName}
                              onChange={(e) => {
                                const newData = tableData.map(item =>
                                  item._id === row._id
                                    ? { ...item, DriverName: e.target.value }
                                    : item
                                );
                                setTableData(newData);
                                filterDrivers(e.target.value, row._id);
                                
                                // Update position when typing
                                const inputElement = driverInputRefs.current[row._id];
                                if (inputElement) {
                                  const position = calculateDropdownPosition(inputElement);
                                  setDropdownPosition(position);
                                }
                                setShowDriverDropdown(true);
                              }}
                              onClick={() => handleDriverInputClick(row._id)}
                              autoComplete="off"
                            />
                          </div>
                        ) : (
                          row.DriverName
                        )}
                      </td>
                      <td>{row.Vendor}</td>
                      <td>{row.DestinationFrom}</td>
                      <td>{row.DestinationTo}</td>
                      <td>{row.TruckType}</td>
                      <td>
                        {editingRow === row._id ? (
                          <select
                            value={row.TransactionStatus}
                            onChange={(e) => {
                              const newData = tableData.map(item =>
                                item._id === row._id
                                  ? { ...item, ...(e.target.value && { TransactionStatus: e.target.value }) }
                                  : item
                              );
                              setTableData(newData);
                            }}
                          >
                            <option value="">Select Transaction Status</option>
                            <option value="Open">Open</option>
                            <option value="Acknowledged">Acknowledged</option>
                          </select>
                        ) : (
                          row.TransactionStatus
                        )}
                      </td>
                      <td>
                        {(editingRow === row._id && userProfile !== 'loadingManager') ? (
                          <input
                            className='editInput'
                            type="number"
                            value={row.Weight}
                            onChange={(e) => {
                              const newData = tableData.map(item =>
                                item._id === row._id
                                  ? { ...item, Weight: e.target.value }
                                  : item
                              );
                              setTableData(newData);
                            }}
                          />
                        ) : (
                          row.Weight
                        )}
                      </td>
                      <td>
                        {editingRow === row._id ? (
                          <input
                            className='editInput'
                            type="number"
                            value={row.ActualWeight}
                            onChange={(e) => {
                              const newData = tableData.map(item =>
                                item._id === row._id
                                  ? { ...item, ActualWeight: e.target.value }
                                  : item
                              );
                              setTableData(newData);
                            }}
                          />
                        ) : (
                          row.ActualWeight
                        )}
                      </td>
                      <td>{row.DifferenceInWeight}</td>
                      {(userProfile !== 'loadingManager' && userProfile !== 'unloadingManager') && <td>{row.Freight}</td>}
                      <td>{row.DieselAmount}</td>
                      <td>{row.DieselSlipNumber}</td>
                      <td>{(row.Freight * (row.TDS_Rate / 100)).toFixed(2)}</td>
                      <td>
                        {editingRow === row._id ? (
                          <input
                            className='editInput'
                            type="number"
                            value={row.Short}
                            onChange={(e) => {
                              const newData = tableData.map(item =>
                                item._id === row._id
                                  ? { ...item, Short: e.target.value }
                                  : item
                              );
                              setTableData(newData);
                            }}
                          />
                        ) : (
                          row.Short
                        )}
                      </td>
                      <td>{row.Advance}</td>
                      <td>{row.Toll}</td>
                      <td>{row.Adblue}</td>
                      <td>{row.Greasing}</td>
                      <td>{row.BillId}</td>
                      {['DieselSlipImage', 'LoadingAdvice', 'InvoiceCompany', 'WeightmentSlip'].map(field => (
                        <td key={field} className="uploadTD">
                          {editingRow === row._id ? (
                            <>
                              <input
                                type="file"
                                name={field}
                                onChange={(e) => handleFileSelect(e, row._id, field)}
                                accept=".pdf,application/pdf"
                                max="1"
                              />
                              {progress[field] > 0 && (
                                <div className="progress-bar">
                                  <div className="progress-fill" style={{ width: `${progress[field]}%` }}></div>
                                </div>
                              )}
                            </>
                          ) : row[field]?.filepath ? (
                            <button
                              className="download-link-btn"
                              onClick={() => handleDownload(row._id, field, row[field].originalname)}
                            >
                              Download {row[field].originalname}
                            </button>
                          ) : (
                            'No file'
                          )}
                        </td>
                      ))}
                      {userProfile !== 'loadingManager' && (
                        <td>
                          {editingRow === row._id ? (
                            <button
                              disabled={disabled}
                              className="save-btn"
                              onClick={() => handleSave(row)}
                            >
                              Save
                            </button>
                          ) : (
                            <button
                              className="edit-btn"
                              onClick={() => handleEdit(row._id)}
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <button className="download-btn" onClick={downloadExcel}>
            Download Excel
          </button>
        </div>
      )}

      {/* Driver Dropdown - Rendered with fixed positioning */}
      {showDriverDropdown && activeDriverRowId && (
        <div
          ref={driverDropdownRef}
          className="TVdropdown"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: dropdownPosition.width ? `${dropdownPosition.width}px` : '200px'
          }}
        >
          {filteredDrivers.length > 0 ? (
            filteredDrivers.map((driver) => (
              <div
                key={driver._id}
                onClick={() => handleDriverSelect(driver, activeDriverRowId)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
              >
                <span style={{ fontWeight: '600' }}>{driver.username}</span>
                {driver.fullName && (
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {driver.fullName}
                  </span>
                )}
              </div>
            ))
          ) : (
            <div
              style={{
                padding: '8px 12px'
              }}
            >
              <span style={{ color: '#666' }}>No drivers found</span>
            </div>
          )}
        </div>
      )}

      {/* MapComponent */}
      {showMapModal && (
        <div className="map-modal-overlay">
          <div className="map-modal">
            <div className="map-modal-header">
              <h3>
                Trip Route: {mapData.tripId}
                {tripDuration[mapData.tripId] && (
                  <span style={{ marginLeft: '10px', fontSize: '14px', color: 'gray' }}>
                    🕒 ETA: {tripDuration[mapData.tripId]}
                  </span>
                )}
              </h3>
              <button
                className="close-modal-btn"
                onClick={() => setShowMapModal(false)}
              >
                ×
              </button>
            </div>
            <div className="map-modal-body">
              <div className="map-container-wrapper">
                <MapComponent
                  from={mapData.from}
                  to={mapData.to}
                  tripId={mapData.tripId}
                  currentLocation={mapData.currentLocation}
                  setTripDuration={setTripDuration}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Floating Freight Map button */}
      {tableData.length > 0 && (
        <button
          className="floating-map-button"
          onClick={handleOpenFreightMap}
          title="View all in-transit freights on map"
        >
          Freight<br />Map
        </button>
      )}
      {showFreightMap && (
        <div className="freight-map-modal">
          <div className="freight-map-modal-content">
            <div className="freight-map-modal-header">
              <h2>In-Transit Freights</h2>
              <button
                className="close-modal-btn"
                onClick={() => setShowFreightMap(false)}
              >
                ×
              </button>
            </div>
            <div className="freight-map-modal-body">
              <FreightMapComponent
                trips={tableData.filter(trip =>
                  trip.DestinationFrom &&
                  trip.DestinationTo &&
                  (trip.TransactionStatus === 'Open' || trip.TransactionStatus === 'Acknowledged' || trip.TransactionStatus === 'Scheduled' || trip.TransactionStatus === 'Billed')
                )}
                setTripDuration={setTripDuration}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodayList;